import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { Typography, Card, CardContent, TableCell, Button } from '@mui/material';
import { withStyles } from '@mui/styles';

import { StoreUtil, DateUtil, QueryUtil, DevRequestUtil } from 'doctivity-shared/utils';

import { DynamicTable } from 'components';

import { queryDevRequests } from 'store/actions/activityLogsActions';

import { withRouter } from 'utils';
import axiosInstance from 'utils/axiosUtil';

/**
 * Admin-only list of dev requests raised from the Key AI chat.
 *
 * Reached by typing /devrequests — deliberately NOT linked from the side menu (see
 * AppNavigation.getAdminItems). The route itself is admin-gated in AppRoot.
 *
 * Rows are ActivityLogs of type AI_DEVREQUEST; the row holds a pointer and the full
 * diagnostic payload lives in S3, fetched on demand through a short-lived presigned URL.
 */

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  condensedCell: {
    padding: 2,
    fontSize: 12,
    fontWeight: 'inherit',
  },
  emptyExpandedRowData: {
    fontSize: 12,
    fontWeight: 'inherit',
  },
  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
  },
  objectDisplay: {
    fontSize: 12,
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  downloadRow: {
    marginTop: theme.spacing(1),
  },
  error: {
    fontSize: 12,
    color: theme.palette.error.main,
  },
});

const prettyPrint = (value) => JSON.stringify(value, null, 2);

// Safe read of the JSON blob in ActivityLogs.context. Older or truncated rows may not
// parse — show the raw string rather than blanking the row.
const parseContext = (row) => {
  if (!row?.context) return null;
  try {
    return JSON.parse(row.context);
  } catch (e) {
    return { unparsed_context: row.context };
  }
};

const columns = [
  {
    label: 'Date & Time',
    key: 'created_at',
    format: DateUtil.formatDateTimeFromDB,
    style: { width: 150 },
  },
  {
    label: 'Category',
    // Derived from the JSON in `context`; the key is a display-only identifier and is
    // not a column on the row (format reads the parsed context instead). It must stay
    // unique — DynamicTable uses column.key as a React key.
    key: 'category',
    // Neither sorting nor filtering can be pushed down to SQL meaningfully here —
    // a LIKE against the raw JSON blob would only confuse.
    sortable: false,
    showFilter: false,
    format: (context, row) => {
      const parsed = parseContext(row);
      return parsed?.category ? DevRequestUtil.categoryLabel(parsed.category) : '';
    },
    style: { width: 170 },
  },
  {
    label: 'Description',
    key: 'description',
  },
  {
    label: 'Errors',
    key: 'server_error_count',
    sortable: false,
    showFilter: false,
    // Backend AI_ERROR count. A non-zero value usually means the cause is already in
    // the file — worth seeing before deciding which request to open.
    format: (context, row) => {
      const parsed = parseContext(row);
      return parsed?.server_error_count ? String(parsed.server_error_count) : '';
    },
    style: { width: 70 },
  },
  {
    label: 'Client',
    key: 'selected_client.name',
    style: { width: 125 },
  },
  {
    label: 'User',
    key: 'user_id',
    // Same filter behavior as the Activity Log page: type a name or an id.
    filterFields: ['user.last_name', 'user.first_name', 'user.id'],
    format: (userId, row) => (row.user ? `${row.user.first_name} ${row.user.last_name}` : ''),
    style: { width: 150 },
  },
];

class DevRequestsPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      query: {
        // Only AI_DEVREQUEST rows — this shares the /ActivityLogs endpoint with the
        // Activity Log page, so the type filter is what makes it a distinct view.
        where: { type: DevRequestUtil.ACTIVITY_TYPE },
        include: [
          {
            association: 'user',
          },
          {
            association: 'selected_client',
            attributes: ['id', 'name'],
          },
        ],
        order: [['created_at', 'DESC']],
        limit: 25,
        offset: 0,
      },
      // reference -> 'loading' | { error } while a presigned URL is being fetched
      downloading: {},
    };
  }

  componentDidMount() {
    const { devRequests } = this.props;

    if (StoreUtil.needsLoadNoCache(devRequests)) {
      this.fetchData();
    }
  }

  fetchData() {
    const { dispatch } = this.props;
    const { query } = this.state;

    // Promote the `user` include to an INNER JOIN while a name/id filter is active,
    // matching the Activity Log page.
    QueryUtil.promoteFilteredIncludes(query.include);

    dispatch(queryDevRequests(query));
  }

  onQueryChange = (query) => {
    this.setState({ query }, this.fetchData.bind(this));
  };

  // Fetch a short-lived presigned URL, then open it. The link is not embedded in the
  // page because it expires in 15 minutes — generating it on click keeps it valid.
  onDownload = async (row, context) => {
    const key = context?.s3_key;
    const reference = context?.reference || row.id;
    if (!key) return;

    this.setState((prev) => ({ downloading: { ...prev.downloading, [reference]: 'loading' } }));

    try {
      // Direct call rather than a redux action — this is a one-off URL with nothing to
      // cache, matching how document downloads work elsewhere (ProviderCommentDetailPage).
      const response = await axiosInstance.get('/DevRequestFile', { params: { key } });
      const url = response?.data?.url;
      if (!url) throw new Error('No download URL returned.');
      // Anchor-click rather than window.open: survives popup blockers after an await.
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.setState((prev) => {
        const next = { ...prev.downloading };
        delete next[reference];
        return { downloading: next };
      });
    } catch (e) {
      this.setState((prev) => ({
        downloading: { ...prev.downloading, [reference]: { error: e?.message || 'Download failed.' } },
      }));
    }
  };

  renderExpandedRow = (row, cols) => {
    const { classes } = this.props;
    const { downloading } = this.state;
    const context = parseContext(row);

    return cols.map((column) => {
      if (column.key !== 'description') {
        return (
          <TableCell
            key={`${row.id}.${column.key}-expanded`}
            className={classes.condensedCell}
            padding='normal'
          />
        );
      }

      if (!context) {
        return (
          <TableCell
            key={`${row.id}.${column.key}-expanded`}
            className={classes.condensedCell}
            padding='normal'
          >
            <Typography className={classes.emptyExpandedRowData}>
              No extra data to show
            </Typography>
          </TableCell>
        );
      }

      const reference = context.reference || row.id;
      const state = downloading[reference];

      return (
        <TableCell
          key={`${row.id}.${column.key}-expanded`}
          className={classes.condensedCell}
          padding='normal'
        >
          <pre className={classes.objectDisplay}>{prettyPrint(context)}</pre>
          <div className={classes.downloadRow}>
            {context.s3_key ? (
              <Button
                size='small'
                variant='outlined'
                disabled={state === 'loading'}
                onClick={() => this.onDownload(row, context)}
              >
                {state === 'loading' ? 'Preparing…' : 'Download full context'}
              </Button>
            ) : (
              <Typography className={classes.error}>
                {context.store_error
                  ? `Context file was not saved: ${context.store_error}`
                  : 'No context file for this request.'}
              </Typography>
            )}
            {state && state.error && (
              <Typography className={classes.error}>{state.error}</Typography>
            )}
          </div>
        </TableCell>
      );
    });
  };

  render() {
    const { devRequests, classes } = this.props;
    const { query } = this.state;

    return (
      <>
        <title>Dev Requests</title>
        <div className={classes.container}>
          <Card>
            <CardContent>
              <DynamicTable
                header={
                  <div className={classes.tableHeader}>
                    <Typography
                      component='h2'
                      variant='h6'
                      color='primary'
                      gutterBottom
                    >
                      Dev Requests
                    </Typography>
                  </div>
                }
                columns={columns}
                colcount={columns.length + 1}
                data={devRequests}
                ContainerType={React.Fragment}
                query={query}
                onQueryChange={this.onQueryChange}
                expandableRows={true}
                rowsPerPageOptions={[25, 50, 100]}
                renderExpandedRow={this.renderExpandedRow}
              />
            </CardContent>
          </Card>
        </div>
      </>
    );
  }
}

DevRequestsPage.propTypes = {
  classes: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  devRequests: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  const { activityLogs } = state;

  return {
    devRequests: StoreUtil.get(activityLogs, 'devrequests_table'),
  };
}

const styled = withStyles(styles)(DevRequestsPage);
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as DevRequestsPage };
